import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  LlmChatSessionDto,
  LlmProviderKind,
  LlmChatSessionStatus,
} from '@paladin-monorepo/common';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { llmChatApi } from '@/lib/api/llm-chat';
import { buildPickerModels, groupModelsByProvider } from '@/lib/llm-model-picker';
import { cn } from '@/lib/utils';

const DEFAULT_MODEL_VALUE = '__default__';
const AGENT_DEFAULT_VALUE = '__agent_default__';

export type LlmModelChoice = {
  provider: LlmProviderKind;
  model: string | null;
};

function encodeValue(provider: LlmProviderKind, model: string | null): string {
  return `${provider}|${model ?? DEFAULT_MODEL_VALUE}`;
}

function decodeValue(value: string): LlmModelChoice {
  const [provider, modelPart] = value.split('|');
  return {
    provider: provider as LlmProviderKind,
    model: modelPart === DEFAULT_MODEL_VALUE ? null : modelPart,
  };
}

interface LlmModelSelectProps {
  provider: LlmProviderKind | null;
  model: string | null;
  onChange: (choice: LlmModelChoice | null) => void;
  /** Shows "Default model" when no provider is selected yet. */
  allowUnset?: boolean;
  disabled?: boolean;
  pending?: boolean;
  triggerClassName?: string;
}

/** Model dropdown used before a chat session exists, and inside an open session. */
export function LlmModelSelect({
  provider,
  model,
  onChange,
  allowUnset = false,
  disabled = false,
  pending = false,
  triggerClassName,
}: LlmModelSelectProps) {
  const { t } = useTranslation();

  const modelsQuery = useQuery({
    queryKey: ['llm-available-models'],
    queryFn: () => llmChatApi.listAvailableModels(),
    staleTime: 5 * 60 * 1000,
  });

  const pickerModels = useMemo(
    () => buildPickerModels(modelsQuery.data ?? [], provider ?? LlmProviderKind.Gemini, model),
    [modelsQuery.data, provider, model],
  );

  const modelsByProvider = useMemo(
    () => groupModelsByProvider(pickerModels),
    [pickerModels],
  );

  const currentValue = provider ? encodeValue(provider, model) : AGENT_DEFAULT_VALUE;
  const isBusy = modelsQuery.isLoading || pending;

  return (
    <Select
      value={currentValue}
      onValueChange={(value) => onChange(value === AGENT_DEFAULT_VALUE ? null : decodeValue(value))}
      disabled={disabled || isBusy}
    >
      <SelectTrigger
        className={cn(
          'h-7 w-auto min-w-[10rem] border-none bg-transparent px-0 text-xs shadow-none focus:ring-0',
          triggerClassName,
        )}
      >
        {isBusy ? (
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t('generic.loading')}
          </span>
        ) : (
          <SelectValue placeholder={t('llmChat.model.default')} />
        )}
      </SelectTrigger>
      <SelectContent>
        {allowUnset && (
          <SelectItem value={AGENT_DEFAULT_VALUE}>{t('llmChat.model.default')}</SelectItem>
        )}
        {Object.values(LlmProviderKind).map((providerKind) => {
          const models = modelsByProvider.get(providerKind) ?? [];
          if (models.length === 0) return null;
          return (
            <SelectGroup key={providerKind}>
              <SelectLabel>{t(`llmChat.model.provider.${providerKind}`)}</SelectLabel>
              <SelectItem value={encodeValue(providerKind, null)}>
                {t('llmChat.model.default')}
              </SelectItem>
              {models.map((m) => (
                <SelectItem key={`${providerKind}:${m.id}`} value={encodeValue(providerKind, m.id)}>
                  {m.isCurrentDisabled
                    ? `${m.displayName ?? m.id} (${t('admin.llmModels.currentDisabled')})`
                    : (m.displayName ?? m.id)}
                </SelectItem>
              ))}
            </SelectGroup>
          );
        })}
      </SelectContent>
    </Select>
  );
}

interface LlmChatModelSelectProps {
  session: LlmChatSessionDto;
  messageCount: number;
}

export function LlmChatModelSelect({ session, messageCount }: LlmChatModelSelectProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const isLocked =
    messageCount > 0 || session.status !== LlmChatSessionStatus.Active;

  const modelsQuery = useQuery({
    queryKey: ['llm-available-models'],
    queryFn: () => llmChatApi.listAvailableModels(),
    staleTime: 5 * 60 * 1000,
    enabled: isLocked,
  });

  const updateMutation = useMutation({
    mutationFn: (choice: LlmModelChoice) =>
      llmChatApi.updateSessionModel(session.id, choice),
    onSuccess: (updated) => {
      queryClient.setQueryData(['llm-chat-session', session.id], (old: unknown) => {
        if (!old || typeof old !== 'object' || !('session' in old)) return old;
        return { ...(old as { session: LlmChatSessionDto; messages: unknown[] }), session: updated };
      });
    },
  });

  const pickerModels = useMemo(
    () => buildPickerModels(modelsQuery.data ?? [], session.provider, session.model),
    [modelsQuery.data, session.provider, session.model],
  );

  const displayLabel = useMemo(() => {
    if (session.model === null) {
      return t('llmChat.model.default');
    }
    const match = pickerModels.find(
      (m) => m.provider === session.provider && m.id === session.model,
    );
    return match?.displayName ?? session.model;
  }, [session.provider, session.model, pickerModels, t]);

  if (isLocked) {
    return (
      <span className="text-xs text-muted-foreground" title={t('llmChat.model.locked')}>
        {t(`llmChat.model.provider.${session.provider}`)} · {displayLabel}
      </span>
    );
  }

  return (
    <LlmModelSelect
      provider={session.provider}
      model={session.model}
      onChange={(choice) => {
        if (choice) updateMutation.mutate(choice);
      }}
      pending={updateMutation.isPending}
    />
  );
}
